import { useState, useRef } from 'preact/hooks';

import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';
import { classNames } from '../utils/UNSAFE_classNames';
import { HelpIcon } from '../PRIVATE_ThemedIcons/HelpIcon';
import { Popup } from '../UNSAFE_Popup';
import { usePopupFocusWithin } from './usePopupFocusWithin';
import {
  helpIconBase,
  helpIconLabelEdgeStart,
  helpContentStyles
} from './themes/UserAssistanceStyles.css';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { LabelRedwoodTheme } from '../UNSAFE_Label/themes/redwood/LabelTheme';

type Props = {
  children?: string;
  id?: string;
  labelEdge?: 'inside' | 'start' | 'top';
  source?: string;
};

export function CompactHelpSource({ children, id, labelEdge, source }: Props) {
  const [isOver, setOver] = useState(false);
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const userAssistance_learnMoreStr = translations.userAssistance_learnMore();
  const { isTabbable, tabbableModeProps } = useTabbableMode();
  const flip = { mainAxis: true, crossAxis: false };
  // TODO: Implement and use a preact Link component instead of using an anchor tag
  const anchorRef = useRef<HTMLAnchorElement>(null);

  const text = children ?? userAssistance_learnMoreStr;

  // helpIconBase references 'Label' vars, so we need to get the baseTheme for 'Label' and include it in the class list
  // so that we pick up the definitions for the labelVars we are using.
  const { baseTheme: labelBaseTheme } = useComponentTheme(LabelRedwoodTheme);

  const styles = classNames([
    labelBaseTheme,
    helpIconBase,
    labelEdge === 'start' && helpIconLabelEdgeStart
  ]);

  const { isFocused: isPopupFocused, popupProps } = usePopupFocusWithin();

  const isOpen = isOver === true || isPopupFocused === true;

  return (
    <>
      <a
        target="_blank"
        {...(source && { href: source })}
        aria-label={text}
        class={styles}
        id={id}
        ref={anchorRef}
        role="link"
        onMouseEnter={() => setOver(true)}
        onMouseLeave={() => setOver(false)}
        onFocus={() => setOver(true)}
        onBlur={() => setOver(false)}
        {...(!isTabbable ? tabbableModeProps : { tabIndex: 0 })}>
        <HelpIcon />
      </a>
      <Popup
        {...popupProps}
        placement="end-top-corner"
        isOpen={isOpen}
        anchorRef={anchorRef}
        flipOptions={flip}
        role="tooltip"
        maxWidth={'296px'}>
        <div class={helpContentStyles}>{text}</div>
      </Popup>
    </>
  );
}
